<template>
  <div>
    <div class="space-y-4">
      <!-- Filters -->
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase"
              >Filters</span
            >
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Passenger search -->
          <div class="relative w-full sm:w-64">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Passenger</label
            >
            <div class="relative mt-1.5">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                v-model="selectedPassenger"
                @keyup="searchPassenger"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search passengers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="passengerSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in passengerSearchResults"
                      :key="result._id"
                      @click="selectPassenger(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Date Range</label
            >
            <div
              class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl"
            >
              <span
                class="pl-3 pr-1 text-base text-gray-500 material-icons-round"
                >event</span
              >
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span
                      class="mx-1 text-base text-gray-300 material-icons-round"
                      >arrow_forward</span
                    >
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected passenger summary -->
      <transition name="fade">
        <div
          v-if="passengerId && selectedPassenger"
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div
            class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md"
          >
            <div class="flex items-center space-x-3">
              <span
                class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-taxiye-100"
              >
                <i class="text-xl text-taxiye-600 material-icons-round"
                  >person</i
                >
              </span>
              <p
                class="text-xs font-extrabold tracking-widest text-gray-600 uppercase"
              >
                Selected Passenger
              </p>
            </div>
            <p class="mt-3 text-2xl font-black leading-none text-gray-900">
              {{ passengerName }}
            </p>
          </div>
          <div
            class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md"
          >
            <div class="flex items-center space-x-3">
              <span
                class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-green-50"
              >
                <i class="text-xl text-green-600 material-icons-round"
                  >account_balance_wallet</i
                >
              </span>
              <p
                class="text-xs font-extrabold tracking-widest text-gray-600 uppercase"
              >
                Balance
              </p>
            </div>
            <p class="mt-3 text-2xl font-black leading-none text-gray-900">
              {{ passengerBalance }}
              <span class="text-xs font-semibold text-gray-400">ETB</span>
            </p>
          </div>
        </div>
      </transition>

      <!-- Incentives table -->
      <div
        class="overflow-hidden bg-white border border-gray-200 rounded-xl shadow-md"
      >
        <div
          class="flex items-center px-5 py-4 space-x-2 text-gray-500 border-b border-gray-200"
        >
          <span class="text-base material-icons-round">redeem</span>
          <h3 class="text-xs font-semibold tracking-widest uppercase">
            Incentive Records / History
          </h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Passenger',
                    'Reason',
                    'Trip Count (Then)',
                    'Trip Fare',
                    'Amount',
                    'Wallet Balance (Then)',
                    'Last Transaction Date',
                    'Status',
                    'Trip',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody v-if="wallets.length" class="relative">
              <tr
                v-for="(wallet, index) in wallets"
                :key="wallet._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div
                    v-if="wallet.passenger"
                    class="flex items-center space-x-3"
                  >
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(wallet.passenger))"
                      >{{ initials(personLabel(wallet.passenger)) }}</span
                    >
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(wallet.passenger, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ wallet.passenger.phoneNumber }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Reason -->
                <td class="px-5 py-4 text-gray-600">
                  <p class="truncate max-w-xs">{{ wallet.reason || "—" }}</p>
                </td>
                <!-- Trip count (then) -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{
                    wallet.passengerTripCount === undefined
                      ? "—"
                      : wallet.passengerTripCount
                  }}
                </td>
                <!-- Trip fare -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <span v-if="wallet.fare !== undefined">
                    {{ (wallet.fare || 0).toFixed(2) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </span>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Amount -->
                <td
                  class="px-5 py-4 font-medium text-gray-900 whitespace-no-wrap"
                >
                  {{ (wallet.amount || 0).toFixed(2) }}
                  <span class="text-xs font-semibold text-gray-400">ETB</span>
                </td>
                <!-- Wallet balance (then) -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    v-if="wallet.currentAmount || wallet.currentAmount == 0"
                    class="font-medium text-gray-900"
                  >
                    {{ wallet.currentAmount.toFixed(2) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </span>
                  <span
                    v-else
                    class="text-xs font-semibold tracking-wider text-gray-400 uppercase"
                  >
                    No record
                  </span>
                </td>
                <!-- Date -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round"
                      >schedule</i
                    >
                    <span>{{ formatDate(wallet.updatedAt) }}</span>
                  </div>
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none uppercase rounded-full"
                    :class="
                      wallet.status === 'paid'
                        ? 'text-green-700 bg-green-100'
                        : wallet.status === 'collected'
                        ? 'text-blue-700 bg-blue-100'
                        : 'text-gray-600 bg-gray-100'
                    "
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="
                        wallet.status === 'paid'
                          ? 'bg-green-500'
                          : wallet.status === 'collected'
                          ? 'bg-blue-500'
                          : 'bg-gray-400'
                      "
                    ></span>
                    {{ wallet.status || "—" }}
                  </span>
                </td>
                <!-- Trip -->
                <td class="px-5 py-4 text-center whitespace-no-wrap">
                  <router-link
                    v-if="wallet.ride"
                    :to="'/rides/' + wallet.ride._id + '/show'"
                    title="View trip"
                    class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <i class="material-icons-round">visibility</i>
                  </router-link>
                  <span v-else class="text-gray-400">—</span>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin"
                  >sync</i
                >
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="9" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i
                      class="text-5xl text-taxiye-500 material-icons-round spin"
                      >sync</i
                    >
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span
                      class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100"
                    >
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500"
                      >No incentive records found</span
                    >
                    <span class="mt-1 text-xs text-gray-400"
                      >Try adjusting your filters</span
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      loading: false,
      wallets: [],
      range: {
        start: null,
        end: null,
      },

      passengers: [],

      passengerSearchResults: [],
      passengerId: null,
      selectedPassenger: null,
      selectedPassengerObj: null,
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    range: {
      handler() {
        this.getAllWallets();
      },
      deep: true,
    },
    currentPage() {
      this.getAllWallets();
    },
    perPage() {
      this.getAllWallets();
    },
    passengerId() {
      this.getAllWallets()
    },
    $route() {
      if (this.$route.query.passenger) {
        this.passengerId = this.$route.query.passenger;
        this.$misc
          .get(`/users/${this.passengerId}`)
          .then(({ data: { passenger } }) => {
            this.selectedPassengerObj = passenger;
          })
          .catch(() => {
            window.flash({
              type: "error",
              body: "Failed to fetch the new balance! please refresh",
            });
          });
      }
    },
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    passengerBalance() {
      if (this.passengerId && this.selectedPassengerObj && this.selectedPassengerObj.balance >= 0)
        return this.selectedPassengerObj.balance.toFixed(2);
      else return " N/A ";
    },
    passengerName() {
      if (this.passengerId && this.selectedPassengerObj && this.selectedPassengerObj.firstName)
        return [
          this.selectedPassengerObj.firstName,
          this.selectedPassengerObj.lastName,
        ].join(" ");
      else return " N/A ";
    },
    invalidTransaction() {
      if (this.paymentType === "bank_deposit") {
        if (
          this.deposit.by &&
          this.deposit.bank &&
          this.deposit.transaction &&
          this.deposit.narrative &&
          this.deposit.date
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
    reportDownloadUrl() {
      return (
        "incentives/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              passenger: this.passengerId ? this.passengerId : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  created() {
    var sd = new Date();
    sd.setDate(0);
    sd.setDate(1);
    var ed = new Date();
    ed.setMonth(ed.getMonth() + 1);
    ed.setDate(0);
    this.range.start = sd;
    this.range.end = ed;
    if (this.$route.query.passenger) {
      this.passengerId = this.$route.query.passenger;
      this.$misc
        .get(`/users/${this.passengerId}`)
        .then(({ data: { passenger } }) => {
          this.selectedPassengerObj = passenger;
        })
        .catch(() => {
          window.flash({
            type: "error",
            body: "Failed to fetch the new balance! please refresh",
          });
        });
    } else {
      this.getAllWallets();
    }
  },
  methods: {
    handleClick() {
      this.adding ? this.add() : this.openAdd();
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "—";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return palette[Math.abs(hash) % palette.length];
    },
    getAllWallets() {
      this.loading = true;
      var path = `/incentives?populate=["passenger","ride"]&page=${this.currentPage}&limit=${this.perPage}`;
      if (this.passengerId) {
        path += "&passenger=" + this.passengerId;
      }
      if (this.by && this.by != "all") {
        path += "&by=" + this.by;
      }
      if (this.range.start) {
        path += "&start=" + this.range.start;
      }
      if (this.range.end) {
        path += "&end=" + this.range.end;
      }
      return this.$misc
        .get(path)
        .then((response) => {
          this.wallets = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.passengerId = null;
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassengerObj = user;
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
